"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  ChevronDown,
  History,
  PanelRightClose,
  RotateCcw,
  X
} from "lucide-react";

import { LoadingState } from "@/components/brand";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { DraftVersionTrigger, UserProfile } from "../../../api/types/draft";

export type TemplateVersionLike = {
  version_id: string;
  template_id: string;
  created_at: string;
  created_by?: string | null;
  created_by_profile?: UserProfile | null;
  template_payload?: Record<string, unknown> | null;
  sections?: unknown[] | null;
  trigger?: DraftVersionTrigger | string | null;
  section_name?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string | null;
  templateTitle: string;
  versions: TemplateVersionLike[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error: string | null;
  restoringVersionId: string | null;
  onRestoreClick: (version: TemplateVersionLike) => void;
};

const toDateMs = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) return value.getTime();
  return null;
};

const formatRelativeTime = (value: unknown): string => {
  const ts = toDateMs(value);
  if (!ts) return "";

  const diffMsRaw = Date.now() - ts;
  const diffMs = Math.max(0, diffMsRaw);

  // Only show relative time for very recent events (<= 10 minutes).
  if (diffMs > 10 * 60_000) {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "";
    }
  }

  if (diffMs < 60_000) {
    const diffSec = Math.max(1, Math.floor(diffMs / 1000));
    return `${diffSec} sec${diffSec === 1 ? "" : "s"} ago`;
  }

  const diffMin = Math.floor(diffMs / 60_000);
  return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
};

const getDisplayName = (
  p: UserProfile | null | undefined,
  fallback: string
) => {
  const first = (p?.first_name ?? "").trim();
  const last = (p?.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const email = (p?.email ?? "").trim();
  if (email) return email;
  return fallback || "Unknown";
};

const getAvatarInitial = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed[0].toUpperCase();
};

type PreviewSection = { title: string; text: string };

const extractPreviewSections = (
  v: TemplateVersionLike
): PreviewSection[] | null => {
  const payload = v.template_payload ?? undefined;
  const rawSections =
    (Array.isArray(v.sections) ? v.sections : null) ??
    (payload && Array.isArray((payload as { sections?: unknown }).sections)
      ? ((payload as { sections?: unknown }).sections as unknown[])
      : null);
  if (!rawSections) return null;

  const result: PreviewSection[] = [];
  rawSections.forEach((sec, idx) => {
    if (!sec || typeof sec !== "object") return;
    const record = sec as Record<string, unknown>;
    const title =
      (typeof record.section_name === "string" && record.section_name.trim()) ||
      (typeof record.title === "string" && record.title.trim()) ||
      (typeof record.name === "string" && record.name.trim()) ||
      `Section ${idx + 1}`;

    const textCandidate =
      (typeof record.sections_after_review === "string" &&
        record.sections_after_review) ||
      (typeof record.section_content === "string" && record.section_content) ||
      (typeof record.text === "string" && record.text) ||
      "";

    result.push({ title, text: textCandidate });
  });

  return result.length ? result : null;
};

const buildPreviewFallback = (v: TemplateVersionLike): string => {
  const payload = v.template_payload;
  if (!payload) return "No content available to preview.";
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return "No content available to preview.";
  }
};

const getVersionSubtitle = (v: TemplateVersionLike): string => {
  if (v.trigger === DraftVersionTrigger.SECTION_REGENERATE) {
    const section = (v.section_name ?? "").trim();
    return section ? `Regenerated ${section}` : "Regenerated a section";
  }
  if (v.trigger === DraftVersionTrigger.FULL_GENERATE) return "Full generate";
  if (v.trigger === DraftVersionTrigger.PRE_RESTORE)
    return "Snapshot before restore";
  if (v.trigger === DraftVersionTrigger.RESTORE) return "Template restored";
  if (v.trigger === DraftVersionTrigger.MANUAL_SECTIONS_EDIT)
    return "Manual edit";
  return "Updated";
};

export function TemplateVersionHistoryDrawer({
  open,
  onOpenChange,
  templateId,
  templateTitle,
  versions,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error,
  restoringVersionId,
  onRestoreClick
}: Props) {
  const sortedVersions = useMemo(() => {
    const copy = [...versions];
    copy.sort((a, b) => {
      const aMs = toDateMs(a.created_at);
      const bMs = toDateMs(b.created_at);
      if (aMs != null && bMs != null) return bMs - aMs;
      if (aMs != null) return -1;
      if (bMs != null) return 1;
      return a.version_id.localeCompare(b.version_id);
    });
    return copy;
  }, [versions]);

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const isPreviewOpen = Boolean(selectedVersionId);

  const versionsScrollRef = useRef<HTMLDivElement | null>(null);
  const [showVersionsScrollHint, setShowVersionsScrollHint] = useState(false);
  const versionsHintRafRef = useRef<number | null>(null);

  const updateVersionsScrollHint = useCallback(() => {
    const el = versionsScrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight - el.clientHeight > 8;
    const atTop = el.scrollTop <= 6;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 6;
    setShowVersionsScrollHint(canScroll && atTop && !atBottom);
  }, []);

  useEffect(() => {
    if (!open) return () => undefined;

    if (versionsHintRafRef.current) {
      window.cancelAnimationFrame(versionsHintRafRef.current);
      versionsHintRafRef.current = null;
    }

    const startedAt = window.performance?.now?.() ?? Date.now();
    const maxMs = 2000;

    const tick = () => {
      updateVersionsScrollHint();
      const now = window.performance?.now?.() ?? Date.now();
      if (now - startedAt < maxMs) {
        versionsHintRafRef.current = window.requestAnimationFrame(tick);
      } else {
        versionsHintRafRef.current = null;
      }
    };

    versionsHintRafRef.current = window.requestAnimationFrame(tick);
    window.addEventListener("resize", updateVersionsScrollHint);

    return () => {
      if (versionsHintRafRef.current) {
        window.cancelAnimationFrame(versionsHintRafRef.current);
        versionsHintRafRef.current = null;
      }
      window.removeEventListener("resize", updateVersionsScrollHint);
    };
  }, [open, isLoading, sortedVersions.length, updateVersionsScrollHint]);

  const selectedVersion = useMemo(() => {
    if (!selectedVersionId) return null;
    return (
      sortedVersions.find((v) => v.version_id === selectedVersionId) ?? null
    );
  }, [sortedVersions, selectedVersionId]);

  const viewingAtLabel = useMemo(() => {
    if (!selectedVersion) return "";
    try {
      const dt = new Date(selectedVersion.created_at);
      return dt.toLocaleString();
    } catch {
      return selectedVersion.created_at;
    }
  }, [selectedVersion]);

  const previewSections = useMemo(() => {
    if (!selectedVersion) return null;
    return extractPreviewSections(selectedVersion);
  }, [selectedVersion]);

  const previewFallbackText = useMemo(() => {
    if (!selectedVersion) return "";
    return buildPreviewFallback(selectedVersion);
  }, [selectedVersion]);

  const previewContentRef = useRef<HTMLDivElement | null>(null);
  const [activePreviewIdx, setActivePreviewIdx] = useState<number>(0);

  const scrollToPreviewSection = useCallback((idx: number) => {
    const root = previewContentRef.current;
    if (!root) return;
    const el = root.querySelector(
      `[data-tpl-preview-section="${idx}"]`
    ) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActivePreviewIdx(idx);
  }, []);

  useEffect(() => {
    const root = previewContentRef.current;
    if (!root) return () => undefined;
    if (!previewSections?.length) return () => undefined;

    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>("[data-tpl-preview-section]")
    );
    if (nodes.length === 0) return () => undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Choose the top-most visible section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => e.target as HTMLElement);
        if (visible.length === 0) return;

        visible.sort((a, b) => {
          const aTop = a.getBoundingClientRect().top;
          const bTop = b.getBoundingClientRect().top;
          return aTop - bTop;
        });

        const idxRaw = visible[0]?.getAttribute("data-tpl-preview-section");
        const idx = idxRaw ? Number(idxRaw) : NaN;
        if (Number.isFinite(idx)) setActivePreviewIdx(idx);
      },
      {
        root,
        threshold: 0.2,
        // Account for the floating badge at top.
        rootMargin: "-96px 0px -70% 0px"
      }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [previewSections]);

  useEffect(() => {
    if (open) return;
    setSelectedVersionId(null);
  }, [open]);

  const currentVersionId = sortedVersions[0]?.version_id ?? null;

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange(false);
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        // With a version open, the space around the panels is part of the
        // reading view rather than a way out of it.
        onInteractOutside={(e) => {
          if (isPreviewOpen) e.preventDefault();
        }}
        className="max-w-none min-w-0 flex-row items-stretch justify-end gap-1.5 bg-transparent p-2 shadow-none data-[side=right]:w-auto data-[side=right]:border-l-0 data-[side=right]:sm:max-w-none"
      >
        {/* Left: Text preview */}
        {isPreviewOpen ? (
          <div className="hidden h-full w-[min(980px,62vw)] min-w-0 flex-none items-stretch md:flex">
            <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl bg-surface shadow-dialog ring-1 ring-line-2">
              <div className="absolute top-3 right-3 z-20">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close preview"
                  onClick={() => setSelectedVersionId(null)}
                >
                  <X />
                </Button>
              </div>
              {viewingAtLabel ? (
                <div className="pointer-events-none absolute top-3 left-3 z-20">
                  <div className="flex items-center gap-2 rounded-md bg-elevated px-3 py-1.5 shadow-popover ring-1 ring-line-3">
                    <span className="text-sm font-medium text-fg">
                      Viewing {templateTitle} at {viewingAtLabel}
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="min-h-0 w-full min-w-0 flex-1 overflow-hidden px-4 pt-14 pb-4">
                {selectedVersion ? (
                  previewSections && previewSections.length ? (
                    <div className="flex h-full min-h-0 w-full min-w-0 items-stretch gap-3">
                      <div className="flex min-h-0 w-70 flex-none flex-col">
                        <span className="pb-2 pl-2 text-sm text-fg-3">
                          Contents
                        </span>
                        <div className="flex min-h-0 w-full flex-1 flex-col gap-0.5 overflow-y-auto pr-1">
                          {previewSections.map((s, idx) => (
                            <button
                              // eslint-disable-next-line react/no-array-index-key
                              key={`toc_${idx}`}
                              type="button"
                              className={cn(
                                "flex h-8 w-full shrink-0 items-center rounded-md px-2 text-left text-base transition-colors duration-100",
                                idx === activePreviewIdx
                                  ? "bg-fill-2 font-medium text-fg"
                                  : "text-fg-2 hover:bg-fill-1 hover:text-fg"
                              )}
                              onClick={() => scrollToPreviewSection(idx)}
                            >
                              <span className="line-clamp-1">{s.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div
                        ref={previewContentRef}
                        className="min-h-0 min-w-0 flex-1 overflow-y-auto"
                      >
                        <div className="flex w-full min-w-0 flex-col gap-4">
                          {previewSections.map((s, idx) => (
                            <div
                              key={`preview_section_${idx}`}
                              data-tpl-preview-section={idx}
                              className="w-full min-w-0 overflow-hidden rounded-lg bg-elevated"
                              style={{ scrollMarginTop: 96 }}
                            >
                              <div className="border-b border-line-1 px-4 py-2">
                                <span className="text-base font-medium text-fg">
                                  {s.title}
                                </span>
                              </div>
                              <div className="px-4 py-3 text-base wrap-break-word whitespace-pre-wrap text-fg">
                                {s.text || "(empty)"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full w-full overflow-y-auto">
                      <pre className="w-full rounded-lg bg-elevated p-4 font-mono text-sm wrap-break-word whitespace-pre-wrap text-fg">
                        {previewFallbackText}
                      </pre>
                    </div>
                  )
                ) : (
                  <LoadingState
                    size="xl"
                    className="h-full w-full"
                    text="Loading preview…"
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Right: Version list */}
        <div className="relative flex h-full w-[min(440px,92vw)] flex-col overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-2">
          <div className="flex w-full items-center justify-start gap-3 border-b border-line-2 px-3 py-3">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close version history"
              onClick={() => onOpenChange(false)}
            >
              <PanelRightClose />
            </Button>
            <div className="flex min-w-0 flex-col items-start gap-0.5">
              <SheetTitle className="truncate">Version History</SheetTitle>
              <SheetDescription className="truncate text-sm">
                View and restore previous versions of this template.
              </SheetDescription>
            </div>
          </div>

          <div
            ref={versionsScrollRef}
            className="flex w-full shrink-0 grow basis-0 flex-col items-start divide-y divide-line-1 overflow-y-auto"
            onScroll={updateVersionsScrollHint}
          >
            {isLoading ? (
              <div className="flex w-full flex-col items-stretch divide-y divide-line-1">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`tpl_version_skel_${idx}`}
                    className="flex w-full flex-col items-stretch px-6 py-4"
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-2">
                        {idx === 0 ? <Skeleton className="h-3 w-24" /> : null}
                        <Skeleton className="h-3 w-18" />
                      </div>
                      <Skeleton className="h-3 w-21" />
                    </div>
                    <div className="flex w-full items-center gap-3 pt-3">
                      <Skeleton className="size-7 shrink-0 rounded-md" />
                      <div className="flex w-full flex-col items-stretch gap-2">
                        <Skeleton className="h-3.5 w-1/2" />
                        <Skeleton className="h-3 w-3/4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex w-full flex-col items-start px-6 py-4">
                <span className="text-base text-danger">{error}</span>
              </div>
            ) : sortedVersions.length === 0 ? (
              <EmptyState
                icon={History}
                title="No Version History"
                description="Version history will appear here as you edit or publish this template."
                className="w-full"
              />
            ) : (
              sortedVersions.map((v, index) => {
                const name = getDisplayName(
                  v.created_by_profile,
                  (v.created_by ?? "").trim()
                );
                const initial = getAvatarInitial(name);
                const timeLabel = formatRelativeTime(v.created_at);
                const isCurrent = index === 0;
                const isSelected = selectedVersionId === v.version_id;
                const message = getVersionSubtitle(v);
                const avatarUrl =
                  v.created_by_profile?.avatar_data_url ?? undefined;

                const isRestoring = restoringVersionId === v.version_id;
                const canRestore =
                  Boolean(v.version_id) && !isRestoring && !isCurrent;

                return (
                  <div
                    key={v.version_id}
                    className={cn(
                      "group flex w-full cursor-pointer flex-col items-start px-6 py-4 transition-colors duration-100",
                      isSelected ? "bg-fill-2" : "hover:bg-fill-1"
                    )}
                    onClick={() => setSelectedVersionId(v.version_id)}
                  >
                    <div className="flex h-7 w-full items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isCurrent ? (
                          <StatusBadge tone="accent">
                            Current Version
                          </StatusBadge>
                        ) : isPreviewOpen && isSelected ? (
                          <StatusBadge tone="neutral" dot>
                            Now Viewing
                          </StatusBadge>
                        ) : null}

                        {timeLabel ? (
                          <span className="text-sm text-fg-3">{timeLabel}</span>
                        ) : null}
                      </div>

                      {isCurrent ? null : (
                        <Button
                          className={cn(
                            "transition-opacity",
                            isSelected || isRestoring
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          )}
                          variant="secondary"
                          size="xs"
                          disabled={!canRestore}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestoreClick(v);
                          }}
                        >
                          {isRestoring ? (
                            <Spinner className="size-3" />
                          ) : (
                            <RotateCcw />
                          )}
                          Restore
                        </Button>
                      )}
                    </div>

                    <div className="flex w-full items-center gap-3 pt-2">
                      <Avatar size="md">
                        {avatarUrl ? (
                          <AvatarImage src={avatarUrl} alt="" />
                        ) : null}
                        <AvatarFallback>{initial}</AvatarFallback>
                      </Avatar>
                      <div className="flex min-w-0 flex-col items-start">
                        <span className="truncate text-base font-medium text-fg">
                          {name}
                        </span>
                        <span className="text-sm text-fg-3">{message}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {hasMore && !isLoading && !error ? (
              <div className="flex min-h-16 w-full items-center justify-center px-6 py-4">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isLoadingMore}
                  onClick={(e) => {
                    e.stopPropagation();
                    onLoadMore();
                  }}
                >
                  {isLoadingMore ? <Spinner className="size-3.5" /> : null}
                  Load More
                </Button>
              </div>
            ) : null}
          </div>

          {showVersionsScrollHint ? (
            <div className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2">
              <div className="flex size-8 animate-bounce items-center justify-center rounded-full bg-elevated text-fg shadow-popover ring-1 ring-line-3 motion-reduce:animate-none">
                <ChevronDown className="size-4" aria-hidden />
              </div>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
